"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.IssueCreationModalContent = exports.IssueCreationModal = void 0;
var _react = _interopRequireWildcard(require("react"));
var _get = _interopRequireDefault(require("lodash/get"));
var _alloyReactNotification = require("@adsk/alloy-react-notification");
var _sdk = require("../../sdk");
var _CreationModal = require("../utils/components/CreationModal");
var _issueFieldSanitization = require("../utils/data/issueFieldSanitization");
var _useInitialType = require("./useInitialType");
var _IssueCreationForm = require("./IssueCreationForm");
var _entitySchemas = require("../utils/validation/entitySchemas");
var _hooks = require("../state/redux/hooks");
var _issuesCollection = require("../state/redux/ducks/issuesCollection");
var _useTranslations = require("../utils/i18n/useTranslations");
var _uuid = require("uuid");
var _useSdk = require("../state/sdk/useSdk");
var _useLocalizedDecimalSeparator = require("../hooks/useLocalizedDecimalSeparator");
var _IssueSnapshot = require("../IssueDetailPanel/IssueDetailTabPanel/hooks/IssueSnapshot");
var _analyticsLib093IssuesEvents = require("../utils/analytics/analyticsLib093IssuesEvents");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const IssueCreationModalContent = _ref => {
  let {
    onClose,
    onCreate,
    onDiscardConfirm,
    onDiscardAbort,
    analyticsData = {},
    fixedTypeId = undefined,
    locationId
  } = _ref;
  const dispatch = (0, _hooks.useDispatch)();
  const sdk = (0, _useSdk.useSdk)();
  const {
    T
  } = (0, _useTranslations.useTranslations)();
  const {
    showNotification
  } = (0, _alloyReactNotification.useNotifications)();
  const [errors, setErrors] = (0, _react.useState)({});
  const {
    sanitizeIssueCustomAttributes
  } = (0, _useLocalizedDecimalSeparator.useLocalizedDecimalSeparator)();
  const {
    uploadSnapshotAsAThumbnail
  } = (0, _IssueSnapshot.useIssueSnapshotFlow)();
  const createEntity = (0, _react.useCallback)(async formValues => {
    const issueSubResources = {};
    setErrors({});
    let createdMarkup;
    try {
      var _analyticsData$refere, _analyticsData$origin;
      const {
        templateId,
        placement,
        snapshotBlob,
        ...issueDataToCreate
      } = (0, _issueFieldSanitization.sanitizeIssueAttributeMap)(formValues);
      if (placement) {
        try {
          const issueId = (0, _uuid.v4)();
          createdMarkup = await sdk.IssueMarkups.create(issueId, placement);
          issueSubResources.placement = createdMarkup;
          issueDataToCreate.id = issueId;
        } catch (e) {
          setErrors({
            ...errors,
            placement: "Failed to create a placement"
          });
          showNotification({
            status: _alloyReactNotification.NOTIFICATION_STATUSES.ERROR,
            message: T.translate("issueCreation.placement.creationError")
          });
          return;
        }
      }

      // mutates the custom attribute objects to avoid having it in memory twice
      sanitizeIssueCustomAttributes(issueDataToCreate.customAttributes);
      const responseIssueData = await dispatch((0, _issuesCollection.createIssueAction)({
        issueData: issueDataToCreate,
        onFailure: error => {
          throw error;
        },
        onSuccess: issue => {
          showNotification({
            status: _alloyReactNotification.NOTIFICATION_STATUSES.SUCCESS,
            message: T.translate("issueCreation.successMessage")
          });
          if (snapshotBlob) {
            uploadSnapshotAsAThumbnail({
              issue,
              snapshotBlob,
              hasMarkups: false,
              trigger: _analyticsLib093IssuesEvents.IssueSnapshotCreateTrigger.CREATE_ISSUE
            });
          }
        },
        analyticsData: {
          ...analyticsData,
          ...issueSubResources,
          referencePicker: (_analyticsData$refere = analyticsData.referencePicker) !== null && _analyticsData$refere !== void 0 ? _analyticsData$refere : "none",
          origin: (_analyticsData$origin = analyticsData.origin) !== null && _analyticsData$origin !== void 0 ? _analyticsData$origin : "issue_log"
        }
      }));
      return [responseIssueData, issueSubResources];
    } catch {
      showNotification({
        status: _alloyReactNotification.NOTIFICATION_STATUSES.ERROR,
        message: T.translate("issueCreation.failureMessage")
      });
      if (createdMarkup) {
        try {
          await sdk.IssueMarkups.delete(createdMarkup.uid);
        } catch {
          // Do nothing
        }
      }
    }
  }, [sanitizeIssueCustomAttributes, dispatch, analyticsData, sdk.IssueMarkups, errors, showNotification, T, uploadSnapshotAsAThumbnail]);

  // This is the last selected type, (retrieved from local storage),
  // or, on first load: "General" . (or the first in the list if "General" doesn't exist)
  // or, the fixed type when fixedTypeId is supplied
  const initialType = (0, _useInitialType.useInitialType)(fixedTypeId);
  const initialValues = {
    title: (0, _get.default)(initialType, "title", ""),
    issueTypeId: (0, _get.default)(initialType, "id", ""),
    status: _sdk.IssueStatus.Open,
    published: true,
    customAttributes: {},
    ...(locationId ? {
      locationId
    } : {})
  };
  const issueValidationSchema = (0, _entitySchemas.useIssueValidationSchema)();
  return /*#__PURE__*/_react.default.createElement(_CreationModal.CreationModalContent, {
    onClose: onClose,
    onCreate: onCreate,
    title: T.translate("issueCreation.modalTitle"),
    errors: errors,
    initialValues: initialValues,
    createEntity: createEntity,
    validationSchema: issueValidationSchema,
    onDiscardConfirm: onDiscardConfirm,
    onDiscardAbort: onDiscardAbort
  }, /*#__PURE__*/_react.default.createElement(_IssueCreationForm.IssueCreationForm, {
    fixedTypeId: fixedTypeId
  }));
};
exports.IssueCreationModalContent = IssueCreationModalContent;
const IssueCreationModal = _ref2 => {
  let {
    isOpen = true,
    ...props
  } = _ref2;
  return /*#__PURE__*/_react.default.createElement(_CreationModal.CreationModalWrapper, {
    isOpen: isOpen
  }, /*#__PURE__*/_react.default.createElement(IssueCreationModalContent, props));
};
exports.IssueCreationModal = IssueCreationModal;
//# sourceMappingURL=IssueCreationModal.js.map